import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ListLayout } from 'components/common/ListLayout';
import TableHeaderItem from 'components/common/ListLayout/TableHeaderItem';
import Api from 'services/api';
import toast from 'react-hot-toast';
import InvoiceIssueModal from 'components/common/InvoiceIssueModal';
// import InvoiceIssueUpdateModal from 'components/common/InvoiceIssueUpdateModal';

const InvoiceIssues = () => {
    const [issues, setIssues] = useState([]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageLength, setPageLength] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({});
    const [loading, setLoading] = useState(false);
    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
    const [selectedIssue, setSelectedIssue] = useState(null);

    const loadIssues = useCallback(async (page = 1, query = {}) => {
        setLoading(true);
        const params = {
            page,
            search: query?.search ?? searchTerm,
            pageLength,
            ...sortConfig,
            ...query,
        };
        const response = await Api.fetchInvoiceIssues(params);

        if (response?.status >= 200 && response?.status < 300) {
            const result = response?.data?.data;
            setIssues(Array.isArray(result?.data) ? result.data : []);
            setTotal(result?.total ?? 0);
            setTotalPages(result?.last_page ?? 1);
        } else {
            toast.error('Unable to load invoice issues.', { className: 'errorToast' });
        }
        setLoading(false);
    }, [searchTerm, sortConfig, pageLength]);

    useEffect(() => {
        loadIssues(currentPage);
    }, [currentPage, loadIssues]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    const fetchList = (page, query) => async () => {
        setCurrentPage(page);
        await loadIssues(page, query);
    };

    const handleUpdateInvoiceIssue = async (data) => {
        setLoading(true);

        const response = await Api.updateInvoiceIssue(data);

        setLoading(false);

        if (response?.status >= 200 && response?.status < 300) {
            toast.success("Invoice issue updated successfully.", {
                className: "successToast",
            });

            setIsUpdateModalOpen(false);
            loadIssues(currentPage);
        } else {
            toast.error(
                response?.data?.message || "Unable to update invoice issue.",
                { className: "errorToast" }
            );
        }
    };

    const renderTableHeader = React.useCallback(() => {
        const callBack = (dir, sortBy) => {
            const nextSort = { sortBy, sortDirection: dir };
            setSortConfig(nextSort);
            loadIssues(currentPage, nextSort);
        };

        return (
            <tr>
                <th>#</th>
                <th>
                    <TableHeaderItem
                        title="Invoice Number"
                        sortBy="invoice_number"
                        sortDirection={sortConfig.sortDirection}
                        callBack={callBack}
                    />
                </th>
                <th>
                    <TableHeaderItem
                        title="Client Name"
                        sortBy="client_name"
                        sortDirection={sortConfig.sortDirection}
                        callBack={callBack}
                    />
                </th>
                <th>
                    <TableHeaderItem
                        title="Date Reported"
                        sortBy="created_at"
                        sortDirection={sortConfig.sortDirection}
                        callBack={callBack}
                    />
                </th>
                <th>Issue Description</th>
                <th>Status</th>
                <th className="col-2 text-center">Action</th>
            </tr>
        );
    }, [sortConfig, currentPage]);

    const renderTableContent = React.useCallback(() => {
        return issues.map((item, index) => (
            <InvoiceIssueItem
                key={item.id}
                data={item}
                index={index}
                current_page={currentPage}
                pageLength={pageLength}
                setSelectedIssue={setSelectedIssue}
                setIsUpdateModalOpen={setIsUpdateModalOpen}
            />
        ));
    }, [issues, currentPage, pageLength]);

    return (
        <div className="content">
            <div className="row_main_head">
                <h2 className="main_heading">Invoice Issues</h2>
            </div>
            <div className="card_table">
                <ListLayout
                    searchPlaceholder="Search Invoice Issues"
                    time={false}
                    addButtonDisabled={true}
                    renderTableHeader={renderTableHeader}
                    renderTableContent={renderTableContent}
                    totalPages={totalPages}
                    fetchList={fetchList}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    loader={loading}
                    noOfItems={total}
                    defaultPageLength={pageLength}
                    pageLengthChange={setPageLength}
                    sortConfig={sortConfig}
                />
            </div>
            
            {/* <InvoiceIssueUpdateModal
                isOpen={isUpdateModalOpen}
                onClose={() => setIsUpdateModalOpen(false)}
                invoiceNumber={selectedIssue?.invoice_number}
                invoiceId={selectedIssue?.id}
                clientName={selectedIssue?.client_name}
                clientId={selectedIssue?.client_id}
                issueDescription={selectedIssue?.message}
                status={selectedIssue?.status}
                onSubmit={handleUpdateInvoiceIssue}
                loading={loading}
            /> */}
            <InvoiceIssueModal
                mode="update"
                isOpen={isUpdateModalOpen}
                onClose={() => setIsUpdateModalOpen(false)}
                invoiceNumber={selectedIssue?.invoice_number}
                invoiceId={selectedIssue?.id}
                clientName={selectedIssue?.client_name}
                clientId={selectedIssue?.client_id}
                issueDescription={selectedIssue?.message}
                status={selectedIssue?.status}
                onSubmit={handleUpdateInvoiceIssue}
                loading={loading}
            />
        </div>
    );
};

const InvoiceIssueItem = ({ data, index, current_page, pageLength, setSelectedIssue, setIsUpdateModalOpen }) => {
    const serial = useMemo(() => {
        return index + 1 + (current_page - 1) * pageLength;
    }, [current_page, pageLength, index]);

    return (
        <tr>
            <td>{serial}</td>
            <td>{data?.invoice_number}</td>
            <td>{data?.client_name}</td>
            <td>
                {data?.created_at
                    ? new Date(data.created_at).toLocaleString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                        hour12: true,
                    })
                    : "-"}
            </td>
            <td>{data?.message}</td>
            <td>
                <span
                    style={{ fontSize: '14px' }}
                    className={`badge ${
                        data?.status === "resolved"
                            ? "bg-success"
                            : data?.status === "rejected"
                            ? "bg-danger"
                            : "bg-warning"
                    }`}
                >
                    {data?.status === "resolved"
                        ? "Resolved"
                        : data?.status === "rejected"
                        ? "Rejected"
                        : "Pending"}
                </span>
            </td>
            <td className="text-center">
                <div className="d-flex align-items-center gap-2 justify-content-center">
                    <Link to={`/invoices/pdf-view/${data?.generated_invoice_id}`} className="main_button text-nowrap">
                        View Invoice
                    </Link>
                    <button
                        type="button"
                        className="main_button"
                        onClick={() => {
                            setSelectedIssue(data);
                            setIsUpdateModalOpen(true);
                        }}
                    >
                        Update Issue
                    </button>
                </div>
            </td>
        </tr>
    );
};

export default InvoiceIssues;